import type Decimal from "decimal.js";
import { z } from "zod";
import type { Requirement } from "./requirement";

export const requirementResultStatusSchema = z.enum(["PENDING", "COMPLETE", "FAILED"]);
export type RequirementResultStatus = z.infer<typeof requirementResultStatusSchema>;

export type RequirementResult = {
  id: string;
  status: RequirementResultStatus;
  questEntryId: string;
  requirementId: string;
  result: Decimal;
  error: string | null;
  isVerifying: boolean;
  callbackUrl: string | null;
  createdAt: Date;
  updatedAt: Date;
  inputTxHash: string | null;
};

export type RequirementResultWithRequirement = RequirementResult & {
  requirement: Requirement;
};

export type SnowflakeTxResult = {
  BLOCK_TIMESTAMP: string;
  TX_ID: string;
  ADDRESS: string;
  VALID: boolean;
  TOKEN_AMOUNT: number;
  FEE_AMOUNT: number;
  CURRENCY: string;
};

export const TransactionResultSchema = z.object({
  blockTimestamp: z.coerce.date(),
  txId: z.string(),
  address: z.string(),
  valid: z.boolean(),
  tokenAmount: z.coerce.number(),
  feeAmount: z.coerce.number(),
  currency: z.string(),
});

export const TransactionResultRawSchema = z.object({
  BLOCK_TIMESTAMP: z.coerce.date(),
  TX_ID: z.string(),
  ADDRESS: z.string(),
  VALID: z.boolean(),
  TOKEN_AMOUNT: z.coerce.number(),
  FEE_AMOUNT: z.coerce.number(),
  CURRENCY: z.string(),
  ERROR: z.string().nullish(),
});
export type TransactionResultRaw = z.infer<typeof TransactionResultRawSchema>;

export const TransactionResultsSchemaRaw = z.array(TransactionResultRawSchema);

/** Schema for Snowflake transaction requirements with explicit uppercase mapping */
export const TransactionResultProcessSchema = z.preprocess((obj) => {
  const data = obj as SnowflakeTxResult;
  return {
    blockTimestamp: data.BLOCK_TIMESTAMP,
    txId: data.TX_ID,
    address: data.ADDRESS,
    valid: data.VALID,
    tokenAmount: data.TOKEN_AMOUNT,
    feeAmount: data.FEE_AMOUNT,
    currency: data.CURRENCY,
  };
}, TransactionResultSchema);

/** Verification result schema for Snowflake transaction requirements. */
export const RequirementTransactionResultSchema = z.object({
  result: TransactionResultProcessSchema,
  error: z.string().optional(),
});
export type RequirementTransactionResult = z.infer<typeof RequirementTransactionResultSchema>;

/** Input schema for adding a requirement result. */
export const RequirementResultInputSchema = z.object({
  result: z.number().optional(),
  status: z.enum(["PENDING", "COMPLETE", "FAILED"]).optional(),
  transaction: TransactionResultSchema.extend({
    walletId: z.string(),
    tokenId: z.string(),
  })
    .omit({ address: true, valid: true, currency: true })
    .optional(),
  error: z.string().optional(),
  callbackUrl: z.string().url().optional(),
  questEntryId: z.string(),
  requirementId: z.string(),
  inputTxHash: z.string().optional(),
});

export const RequirementUserInputSchema = z.object({
  txHash: z.string().optional(),
});
export type RequirementUserInput = z.infer<typeof RequirementUserInputSchema>;

export type RequirementResultInput = z.infer<typeof RequirementResultInputSchema>;
